/**
 * Rate Card editor (Platform Admin) — the price book behind customer invoices.
 *
 * Sets unit prices for the four billable axes the invoice assembler reads:
 *   • MODULE        — price per sellable module (CORE is foundational, never billed)
 *   • INSTANCE_BASE — base fee per compute size (XS…XL)
 *   • USAGE         — price per metered unit (executions, tasks, governed calls, …)
 *   • SERVICE_PLAN  — price per support tier
 *
 * Two scopes: "Platform default" (customer_id NULL) and "This customer" (per-customer
 * override). In customer scope the platform default is shown as a read-only fallback hint,
 * so an empty override transparently inherits the default. Self-Hosted instances bill the
 * resolved price × the self-hosted multiplier — applied server-side, not here.
 *
 * Writes are Platform Admin only (the backend is authoritative; this is the UX surface).
 * Mirrors the catalog enums (modules / sizes / meters / tiers) the platform defines, the
 * same way the quotas card mirrors QuotaKey.
 */
import React, { useEffect, useState } from 'react';
import { Tag } from 'lucide-react';
import { Button } from './Button';
import { Pill } from './Pill';
import { Card } from './Card';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const CURRENCY = 'USD';

// Catalog — mirrors the platform's billable item keys. Grouped by RateKind.
const MODULES = [
  { key: 'WORKFLOW_ORCH', label: 'M1 · Workflow Orchestration' },
  { key: 'WORKFLOW_PROTECT', label: 'M2 · Workflow Protection' },
  { key: 'GOVERNANCE', label: 'M3 · Protection Layer' },
  { key: 'AGENT_GOV', label: 'M4 · Agent Protection' },
  { key: 'EXT_AGENT_GOV', label: 'M5 · External Agent Protection' },
  { key: 'SHADOW_DISCOVERY', label: 'M6 · Shadow AI Discovery' },
  { key: 'TRUST_COMPLIANCE', label: 'M7 · Trust & Compliance Center' },
  { key: 'IDENTITY_AUTHZ', label: 'M8 · Identity & Authorization' },
  { key: 'API_GATEWAY', label: 'M9 · Protected Gateway & API Access' },
  { key: 'PROVE7_CONTAINER', label: 'M10 · Prove7 Standalone Container' },
];
const SIZES = [
  { key: 'XS', label: 'XS' }, { key: 'S', label: 'S' }, { key: 'M', label: 'M' },
  { key: 'LG', label: 'LG' }, { key: 'XL', label: 'XL' },
];
const METERS = [
  { key: 'WORKFLOW_EXECUTIONS', label: 'Workflow executions', unit: 'per execution' },
  { key: 'TASKS', label: 'Tasks', unit: 'per task' },
  { key: 'GOVERNED_EXECUTIONS', label: 'Protected executions', unit: 'per call' },
  { key: 'CONCURRENT_WORKFLOWS', label: 'Concurrent workflows', unit: 'per concurrent' },
  { key: 'API_REQUESTS', label: 'API requests', unit: 'per request' },
  { key: 'STORAGE_GB', label: 'Storage', unit: 'per GB' },
];
const TIERS = [
  { key: 'STANDARD', label: 'Standard' }, { key: 'PREMIUM', label: 'Premium' }, { key: 'PREMIER', label: 'Premier' },
];
// Managed-model margin — a multiplier on the metered provider cost, not a price.
const MARGINS = [
  { key: 'LLM_COST_USD', label: 'Managed model usage', unit: '× provider cost' },
];

const GROUPS = [
  { kind: 'MODULE', title: 'Modules', note: 'Price per entitled module.', rows: MODULES },
  { kind: 'INSTANCE_BASE', title: 'Instance base fee', note: 'Per compute size, per billing period.', rows: SIZES },
  { kind: 'USAGE', title: 'Usage', note: 'Per metered unit consumed in the period.', rows: METERS },
  { kind: 'SERVICE_PLAN', title: 'Service plan', note: 'Per support tier.', rows: TIERS },
  {
    kind: 'LLM_MARGIN', title: 'Managed model margin', valueKind: 'multiplier',
    note: 'Multiplier on managed model provider cost — 1.30 means cost plus 30%. Bring-your-own-key spend is billed by the provider and never invoiced here.',
    rows: MARGINS,
  },
];

const inputCls = 'h-9 px-2.5 rounded-v2-sm border border-v2-border-default bg-v2-surface text-[13px] text-v2-ink focus:outline-none focus:border-v2-action';
const mapKey = (kind, itemKey) => `${kind}::${itemKey}`;

// Parse a money string → non-negative number, or null when blank/invalid.
const parsePrice = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim().replace(/[,$\s]/g, '');
  if (s === '') return null;
  if (!/^\d+(\.\d+)?$/.test(s)) return NaN;
  return parseFloat(s);
};
const fmtPrice = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`);
// Margin multiplier: shown as ×N (e.g. ×1.30), not a currency amount.
const fmtMult = (n) => (n == null ? '—' : `×${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export function RateCardCard({ customerId }) {
  const { addToast } = useToast();
  const [scope, setScope] = useState('default'); // 'default' | 'customer'
  const [prices, setPrices] = useState({});       // mapKey → unitPrice (the active scope)
  const [defaults, setDefaults] = useState({});    // mapKey → unitPrice (platform defaults, hint)
  const [edits, setEdits] = useState({});          // mapKey → raw string
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const buildMap = (rows) => {
    const m = {};
    (rows || []).forEach((r) => { m[mapKey(r.kind, r.itemKey)] = r.unitPrice; });
    return m;
  };

  const load = async () => {
    setLoading(true);
    try {
      // Always know the platform defaults (the fallback when a customer override is empty).
      const defRes = await api.getRateCard(null);
      const defMap = buildMap((defRes && defRes.data ? defRes.data : defRes) || []);
      setDefaults(defMap);

      if (scope === 'customer' && customerId) {
        const ovRes = await api.getRateCard(customerId);
        setPrices(buildMap((ovRes && ovRes.data ? ovRes.data : ovRes) || []));
      } else {
        setPrices(defMap);
      }
      setEdits({});
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Failed to load rate card' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, customerId]);

  const setEdit = (kind, itemKey, raw) =>
    setEdits((m) => ({ ...m, [mapKey(kind, itemKey)]: raw }));

  // Display value: the operator's in-progress edit, else the saved price for the active scope.
  const displayVal = (kind, itemKey) => {
    const k = mapKey(kind, itemKey);
    if (k in edits) return edits[k];
    const v = prices[k];
    return v == null ? '' : String(v);
  };

  const save = async () => {
    // Collect changed rows that parse to a valid non-negative number.
    const changes = [];
    for (const g of GROUPS) {
      for (const r of g.rows) {
        const k = mapKey(g.kind, r.key);
        if (!(k in edits)) continue;
        const parsed = parsePrice(edits[k]);
        if (parsed == null) continue;        // blank — skip (no delete semantics in v1)
        if (Number.isNaN(parsed)) {
          addToast({ tone: 'danger', message: `Invalid price for ${r.label}` });
          return;
        }
        changes.push({ kind: g.kind, itemKey: r.key, unitPrice: parsed });
      }
    }
    if (changes.length === 0) {
      addToast({ tone: 'warning', message: 'No price changes to apply' });
      return;
    }
    setSaving(true);
    try {
      const target = scope === 'customer' ? customerId : null;
      for (const c of changes) {
        const res = await api.upsertRateCard({ customerId: target, kind: c.kind, itemKey: c.itemKey, unitPrice: c.unitPrice, currency: CURRENCY });
        if (res && res.ok === false) throw new Error(res.error || 'Price update failed');
      }
      addToast({ tone: 'success', message: `${changes.length} price${changes.length > 1 ? 's' : ''} updated` });
      await load();
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Price update failed' });
    } finally {
      setSaving(false);
    }
  };

  const inCustomerScope = scope === 'customer';

  return (
    <Card padding="sm">
      <Card.Body>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <Tag size={13} className="text-v2-action-strong" />
            <div className="text-[12px] font-semibold text-v2-ink">Pricing — rate card</div>
          </div>
          <Pill tone="muted" size="sm">{CURRENCY}</Pill>
        </div>
        <div className="text-[11px] text-v2-ink-muted mb-2">
          Unit prices behind the invoice. Blank inherits the platform default. Self-Hosted instances
          bill at the resolved price × the self-hosted multiplier (applied automatically).
        </div>

        {/* Scope toggle */}
        <div className="flex gap-2 mb-3">
          {[['default', 'Platform default'], ['customer', 'This customer']].map(([k, l]) => (
            <Button key={k} size="sm" variant={scope === k ? 'primary' : 'secondary'}
              onClick={() => setScope(k)} disabled={k === 'customer' && !customerId}>{l}</Button>
          ))}
        </div>

        {loading && <div className="text-v2-ink-muted text-[12px]">Loading prices…</div>}

        {!loading && GROUPS.map((g) => (
          <div key={g.kind} className="mb-3">
            <div className="text-[11px] font-semibold text-v2-ink mb-0.5">{g.title}</div>
            <div className="text-[10.5px] text-v2-ink-muted mb-1">{g.note}</div>
            <table className="w-full text-[12px] table-fixed">
              <thead>
                <tr className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider">
                  <th className="text-left font-semibold py-1 w-[48%]">Item</th>
                  <th className="text-right font-semibold py-1 w-[32%] pr-1">
                    {g.valueKind === 'multiplier' ? 'Multiplier' : 'Unit price'}
                  </th>
                  <th className="text-left font-semibold py-1 pl-2 w-[20%]">
                    {inCustomerScope ? 'Default' : 'Unit'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r) => {
                  const def = defaults[mapKey(g.kind, r.key)];
                  const isMult = g.valueKind === 'multiplier';
                  return (
                    <tr key={r.key} className="border-t border-v2-border-subtle align-top">
                      <td className="py-1.5 text-v2-ink">{r.label}</td>
                      <td className="py-1.5 pr-1">
                        <div className="flex items-center gap-1 justify-end">
                          <span className="text-v2-ink-muted text-[12px]">{isMult ? '×' : '$'}</span>
                          <input type="text" inputMode="decimal" className={`${inputCls} w-full text-right`}
                            value={displayVal(g.kind, r.key)} placeholder="—"
                            onChange={(ev) => setEdit(g.kind, r.key, ev.target.value)} />
                        </div>
                      </td>
                      <td className="py-1.5 pl-2 text-v2-ink-muted">
                        {inCustomerScope ? (isMult ? fmtMult(def) : fmtPrice(def)) : (r.unit || '')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

        {!loading && (
          <div className="flex items-center gap-2 mt-1">
            <Button variant="primary" size="sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : `Apply prices${inCustomerScope ? ' (override)' : ''}`}
            </Button>
            {inCustomerScope && (
              <span className="text-[11px] text-v2-ink-muted">Editing this customer’s overrides.</span>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default RateCardCard;
